<script lang="ts" setup>
import dayjs from 'dayjs'
import Dialog from '@/components/dialog/dialog-modal/dialog.vue'
import InfoGift from '~/pages/goods/components/info-gift.vue'
import Toast from '@/components/toast'
import { TOAST, useCustomToastStore } from '@/components/custom-toast'
import { SkeletonGoodsDetailImg, SkeletonGoodsDetailInfo } from '@/components/loading'
import { scrollTo } from '@/utils/interaction/scroll'
import { getDateWithDayFormat } from '@/utils/date'
import { commaizeNumber } from '@/utils/primitive/number'
import { useUiScrollStore, useUserStore, useUiLayoutStore } from '~/store'
import { useUtilDetectAllStore, useGateStore, ContributeOrder } from '~/store/common'
import { useGoodsStore, useOptionsStore } from '~/store/store-goods'
import { useRootAppStore } from '~/store/app'
import { useGoogleTagManagerStore } from '~/store/marketing/google-tag-manager'
import { VideoStateKey, TooltipStateKey, useRecobell, useDetailGaEvent } from '~/pages/goods/composables/ui'
import { useVisitHistory, VisitHistoryProduct } from '~/composable/utils/use-visit-history'
import { useBroadcastTime } from '~/composable/store/use-broadcast-time'
import { useHackle } from '~/composable/marketing/use-hackle'
// import { useTimerOld } from '~/composable/utils'
import { useHead } from '@unhead/vue'
import { useMeta } from '~/composable/utils/use-meta'
import { env } from '@/composables/utils/use-environment'
import { postWishList, deleteWishList, getMediaRelationList, getBundleDelivery } from '~/api/goods'
import { GA_EVENT_TYPE } from '~/composable/store/ga/use-store-ga-e-commerce'
import {
  GoodsInfo,
  ItemInfo,
  AlcoholInfo,
  BundleDeliveryRequest,
  InfoGiftItem,
  MediaRelationListResponse
} from '~/api/types/goods'
import { deleteBroadCastAlarm } from '~/api/customer'
import { getSearchRecommandGoods } from '~/api/search'
import { GoodsListItem, GroupCodeDetail, GroupCodeGoodsListRequest } from '~/api/types/display'
import { getUsePossToll } from '~/api/event'
import { PRODUCT_CODE } from '~/pages/goods/constants'
import { CART_FLAG } from '~/constants/common'
import { RC_CODE } from '~/constants/common/recobell'
import DetailInfo from '~/pages/goods/components/detail-info.vue'
import DetailImg from '~/pages/goods/components/detail-img.vue'
import ReviewList from '~/pages/goods/components/review-list.vue'
import InquiryList from '~/pages/goods/components/inquiry-list.vue'
import InfoDetailImg from '~/pages/goods/components/info-detail-img.vue'
import Nlive from '~/pages/goods/components/nlive.vue'
import ModalBroadCast from '~/pages/goods/components/layer/modal-notify-broadcast.vue'
import ModalShare from '~/pages/goods/components/layer/modal-share.vue'
import InfoAlcohol from '~/pages/goods/components/info-alcohol.vue'
import InfoTable from '~/pages/goods/components/info-table.vue'
import Delivery from '~/pages/goods/components/delivery.vue'
import ModalOptions from '~/pages/goods/components/layer/modal-options.vue'
import Options from '~/pages/goods/components/options.vue'
import GoodsButtonGroup from '~/pages/goods/components/goods-button-group.vue'
import VideosPlaying from '~/pages/goods/components/videos-playing.vue'
import HeadCopyBanner from '~/pages/goods/components/head-copy-banner.vue'
import GoodsRecommend from '~/components/common/goods-recommend.vue'
import GoodsMdRecommend from '~/pages/goods/components/goods-md-recommend.vue'
import GoodsMdList from '~/pages/goods/components/goods-md-list.vue'
import Kakao from '~/pages/goods/components/kakao-buy-btn.vue'
import { useCommonGoodsBannerStore } from '~/store/common/index'
import CommonGoodsBanner from '~/components/layer/common-goods-banner.vue'
import { useContributeOrderStore } from '~/store/common'
import ModalDetailCart from '~/pages/goods/components/layer/modal-detail-cart.vue'
import { hasNotOptionCartEvent } from '~/composable/common/goods/use-add-cart'
import { useClockStore } from '~/store/common/clock'
import { useTimerFormat } from '~/composable/utils/use-timer-format'
import { VIRTUAL_DISP_ID } from '~/constants/common'
import { getGroupCodeGoodsList } from '~/api/display'
import { getGoodsDetail } from '~/api/goods'

type Key = (typeof TAB_TYPE)[keyof typeof TAB_TYPE]
interface Reactive {
  tabItems: { id: Key; title: string; number: string | number }[]
  activeTab: Key
  isShowOptions: boolean
  isShowBroad: boolean
  isShowCoupon: boolean
  isShowShare: boolean
  isWishBtn: boolean
  isBundleDelivery: boolean
  hasAlarm: boolean
  liveOrdQty: number
  ptnCd: string
}

const emit = defineEmits<{
  (event: 'onCart', result: boolean): void
  (event: 'update:modelValue', result: boolean): void
}>()

const isShowModalDetailCart = ref<boolean>(false)
const cartKey = ref<number>(1)
const cartInfo = ref()

// use store
const route = useRoute()
const router = useRouter()
const customToastStore = useCustomToastStore()
const uiScrollStore = useUiScrollStore()
const goodsStore = useGoodsStore()
const detectAllStore = useUtilDetectAllStore()
const rootAppStore = useRootAppStore()
const { goods, nLiveBroadCast, buttonStateInfo, broadCastAlarm, broadCastAlarmDisabled } = storeToRefs(goodsStore)
const { isDesktop } = storeToRefs(detectAllStore)
const { addVisitHistory } = useVisitHistory()
const { currentTime } = storeToRefs(rootAppStore)
const userStore = useUserStore()
const optionsStore = useOptionsStore()
const { storeOptionsInfo, storeGoodsInfo } = storeToRefs(optionsStore)
const { togetherViewCohortParams, /* categoryGoodsParams,  */ popularGoodsParams } = useRecobell()
const gateStore = useGateStore()
const { setCustomMeta } = useMeta()
const googleTagManagerStore = useGoogleTagManagerStore()
const { setPageTitle, setDepths } = googleTagManagerStore
const { contributeSearchKeyword } = storeToRefs(googleTagManagerStore)
const { addDetailGoogleAnalytics } = useDetailGaEvent()
const uiLayoutStore = useUiLayoutStore()
const commonGoodsBannerStore = useCommonGoodsBannerStore()
const contributeOrderStore = useContributeOrderStore()
const { contributeOrder } = storeToRefs(contributeOrderStore)

const clockStore = useClockStore()

const { variationDetail } = useHackle()
const abTestCodeRef = ref<string>('')

defineOptions({
  name: 'DetailCaching'
})

type MountedInfo = {
  goodsCd?: string
}
const mountedInfo = ref<MountedInfo>({
  goodsCd: undefined
})

// 페이지 첫 진입 유무
const isFirstEntryInto = ref<boolean | undefined>(undefined)
const onWatched = ref<boolean>(false)
const isImgLoaded = ref<boolean>(false)

// provide videoState
const videoState = reactive({
  isShowVideo: false,
  isPlaying: isDesktop.value ?? false,
  isPause: false,
  isShowVideoNav: false,
  isVideoArea: true
})
provide(VideoStateKey, { ...toRefs(videoState) })

// provide tooltipState
const tooltipState = reactive({
  tooltipItems: new Map()
})
provide(TooltipStateKey, { ...toRefs(tooltipState) })

// variables
const dialogRef = ref<InstanceType<typeof Dialog>>()
const detailInfoWrap = ref<HTMLElement | null>(null)
const gcGoodsInfo = ref<GroupCodeDetail>()
const gcGoodsList = computed<Array<GoodsListItem>>(() => gcGoodsInfo.value?.gcGoodsList ?? [])
const togetherView = ref<Array<GoodsListItem>>([])
// const categoryGoods = ref<Array<GoodsListItem>>([])
const popularGoods = ref<Array<GoodsListItem>>([])
const relationGoods = ref<Array<MediaRelationListResponse>>([])
const infrId = ref<number>(0)
// const liveOrdQtyInterval = ref<ReturnType<typeof setInterval>>()
const TAB_TYPE = { TAB1: 'TAB1', TAB2: 'TAB2', TAB3: 'TAB3', TAB4: 'TAB4' } as const
const viewType = { RIGHT: 'RIGHT' } as const
const state = reactive<Reactive>({
  tabItems: [
    { id: TAB_TYPE.TAB1, title: '상품설명', number: '' },
    { id: TAB_TYPE.TAB2, title: '상품정보', number: '' },
    { id: TAB_TYPE.TAB3, title: '상품평', number: '' },
    { id: TAB_TYPE.TAB4, title: '상품문의', number: '' }
  ],
  activeTab: TAB_TYPE.TAB1,
  isShowCoupon: false,
  isShowOptions: false,
  isShowBroad: false,
  isShowShare: false,
  isWishBtn: false,
  isBundleDelivery: false,
  hasAlarm: false,
  liveOrdQty: 0,
  ptnCd: ''
})
const goodsCd = route.params.goodsCd

const bundleInfo = reactive<BundleDeliveryRequest>({
  dlvGoodsCd: Number(goodsCd),
  pageNum: 1,
  pageSize: 10,
  searchTerm: '',
  sortCriteria: 'BS'
})
const alcoholInfo = ref<AlcoholInfo>()
const giftInfo = ref<InfoGiftItem>()
const itemInfo = ref<ItemInfo>()
const goodsInfo = ref<GoodsInfo>()

const toastHandler = (content: string, delay: number, position: string, useHtml?: boolean) => {
  Toast({ content, delay, position, useHtml })
}

const isLogin = computed<string>(() => userStore.getUserState.custInfo?.loginId)
const isDirectVisitEnabled = computed(() =>
  ['130', '481', '538', '536', '539', '580'].includes(gateStore.getRequestCode().ptnCd)
)

const broadcastTimeInfo = computed(() => {
  if (goods.value?.brdctInfo) {
    const start = goods.value?.brdctInfo?.formStartDttm
    const end = goods.value?.brdctInfo.formEndDttm

    if (start && end && currentTime.value) {
      return useBroadcastTime(start, end, currentTime.value)
    } else {
      return null
    }
  }
})

const isTimeOver = ref<boolean>(false)
const timerCallback = () => {
  isTimeOver.value = true
}

// const { state: timerState, startTimer, clearTimer } = useTimerOld()
const remainTime = computed<string>(() => {
  // if (timerState.min <= 0 && timerState.sec <= 0) {
  //   return '00:00:00'
  // }
  //
  // const hour = Math.floor((timerState.min % (60 * 24)) / 60)
  //   .toString()
  //   .padStart(2, '0')
  // const minute = (timerState.min % 60).toString().padStart(2, '0')
  // const second = timerState.secView
  //
  // return `${hour}:${minute}:${second}`
  if (!isLiveGoods.value || !goods.value?.brdctInfo?.formEndDttm) {
    return '00:00:00'
  }

  const { secondsToTimeFormat } = useTimerFormat()
  const remainingSeconds = clockStore.getRemainingSeconds(goods.value.brdctInfo.formEndDttm)
  if (remainingSeconds < 0) {
    timerCallback()
    return '00:00:00'
  }
  return secondsToTimeFormat(remainingSeconds)
})

const hasAlarm = computed(() => {
  if (!isLogin.value) {
    broadCastAlarm.value = false
    return false
  }

  if (broadCastAlarm.value) {
    state.hasAlarm = true
    infrId.value = Number(buttonStateInfo.value?.infrId)
    return true
  } else {
    state.hasAlarm = false
    infrId.value = Number(buttonStateInfo.value?.infrId)
    return false
  }
})

/** 방송 여부 */
const isLiveGoods = computed<boolean>(() => goods.value?.brdctInfo?.liveCd === 'A' && !isTimeOver.value)
const isLimitGoods = computed<boolean>(() => goods.value?.brdctInfo?.brdctLimitYn === 'Y')
const isLiveState = computed(() => {
  const brdctInfo = goods.value?.brdctInfo
  if (brdctInfo?.liveCd === 'A') {
    return remainTime.value
  } else if (brdctInfo?.liveCd === 'B') {
    const brdctDate = dayjs(brdctInfo?.formStartDttm).format('YYYYMMDD')
    return getDateWithDayFormat(brdctDate, brdctInfo?.formStartDttm)
  }
})

/** 상담상품 여부 */
const isConsultantGoods = computed<boolean>(() => {
  const isDispTypedCd = goods.value?.detailInfo?.dispTypeCd
  if (isDispTypedCd) {
    return PRODUCT_CODE.GOODS_DISPLAY_TYPE.CONSULTATION.includes(isDispTypedCd)
  } else {
    return false
  }
})

const isBroadCastGoods = computed<boolean>(
  () => goods.value?.detailInfo?.busChnId === 'TV' || goods.value?.detailInfo?.busChnId === 'CTCOM'
)
const nliveCd = computed(() => goods.value?.detailInfo?.nliveCd)
const isAdltGoods = computed<boolean>(() => goods.value?.detailInfo?.adltGoodsYn === 'Y')
const isAdultCertifiedUser = computed<boolean>(() => userStore.getUserState?.custInfo?.adltCertYn === 'Y')
const isBundleGoods = computed<boolean>(() => goods.value?.dlvrInfo?.dlvrSaveGoodsYn === 'Y')
const isNoSaleMall = computed<boolean>(() => goods.value?.detailInfo?.noSaleMallYn === 'Y')
const isOnSiteSaleGoodsYn = computed<boolean>(() => goods.value?.detailInfo?.onSiteSaleGoodsYn === 'Y')
const channelId = computed(() => goods.value?.detailInfo?.busChnId)
const broadcastLiveCd = computed(() => goods.value?.brdctInfo?.liveCd)
const recomdealYn = computed<boolean>(() => goods.value?.detailInfo?.recomDealYn === 'Y')

const headCopyTopBanner = computed(() =>
  goods.value?.headCopyInfo?.headCopyList?.filter((v: { epsuSpr: string }) => v.epsuSpr === 'GDS_TOP')
)

const headCopyMidBanner = computed(() =>
  goods.value?.headCopyInfo?.headCopyList?.filter((v: { epsuSpr: string }) => v.epsuSpr === 'GD_BTM')
)

const headCopyBottomBanner = computed(() =>
  goods.value?.headCopyInfo?.headCopyList?.filter((v: { epsuSpr: string }) => v.epsuSpr === 'TAB_TOP')
)

const headCopyDeliveryText = computed(() =>
  goods.value?.headCopyInfo?.headCopyList?.filter((v: { epsuSpr: string }) => v.epsuSpr === 'DLVR_BTM')
)

const headCopyRecommendTop = computed(() =>
  goods.value?.headCopyInfo?.headCopyList?.filter((v: { epsuSpr: string }) => v.epsuSpr === 'RCMD_TOP')
)

const recommendTopSeq = computed(() => {
  if (headCopyRecommendTop.value && headCopyRecommendTop.value.length > 0) {
    return headCopyRecommendTop.value[0].hdcpSeq
  }
})
const recommendTopText = computed(() => {
  if (headCopyRecommendTop.value && headCopyRecommendTop.value.length > 0) {
    return headCopyRecommendTop.value[0].rcmdGoodsText
  }
})
const recommendTopType = computed(() => {
  if (headCopyRecommendTop.value && headCopyRecommendTop.value.length > 0) {
    return headCopyRecommendTop.value[0].goodsRankUseYn === 'Y'
  }
})

const headCopyRecommendGds = computed(() =>
  goods.value?.headCopyInfo?.headCopyList?.filter((v: { epsuSpr: string }) => v.epsuSpr === 'RCMD_GDS')
)

const recommendGdsList = computed(() => {
  if (headCopyRecommendGds.value && headCopyRecommendGds.value.length > 0) {
    return headCopyRecommendGds.value[0].rcmdGoodsList
  }
})
const recommendGdsText = computed(() => {
  if (headCopyRecommendGds.value && headCopyRecommendGds.value.length > 0) {
    return headCopyRecommendGds.value[0].rcmdGoodsText
  }
})
const recommendGdsType = computed(() => {
  if (headCopyRecommendGds.value && headCopyRecommendGds.value.length > 0) {
    return headCopyRecommendGds.value[0].goodsRankUseYn === 'Y'
  }
})

const headCopyHtmlInfo = computed(() =>
  goods.value?.headCopyInfo?.headCopyList?.find((v: { hdcpType: any }) => v.hdcpType === '5')
)

/** 방송중 구매/상담가능 */
const isLiveLimit = computed<boolean>(() => goods.value?.detailInfo?.stockOutFlag === 'B')
const isNoOption = computed<boolean>(() => storeOptionsInfo.value?.goodsOptionInfo?.unitList?.length === 0)
const isSoldOut = computed<boolean>(
  () =>
    goods.value?.detailInfo?.stockOutFlag === 'S' || goods.value?.detailInfo?.stockOutFlag === 'R' || isNoOption.value
)

const goodsImagePath = computed(() => {
  const photoList = goods.value?.detailInfo?.photoList
  if (photoList && photoList.length > 0) {
    return photoList[0].photoPath
  }
})

const localContributeOrder = ref<ContributeOrder>({})

const isEasm = computed(
  () =>
    isLogin.value &&
    (userStore.getUserState.custInfo.nsEasmYn === 'Y' || userStore.getUserState.custInfo.harimEasmYn === 'Y')
)

watch(
  () => route.params.goodsCd,
  async goodsCd => {
    isShowModalDetailCart.value = false
    if (goodsCd && Number(goodsCd) !== Number(goods.value?.detailInfo?.goodsCd)) {
      if (goods.value?.detailInfo?.goodsCd) {
        onWatched.value = true
      }
      if (route.query.type === 'order') return

      if (state.isShowOptions) {
        state.isShowOptions = false
      }

      await init(Number(goodsCd))
    }

    if (route.name === 'GoodsDetail' && Number.isInteger(Number(goodsCd)) && goodsCd.length < 15) {
      commonGoodsBannerStore.commomBannerGoodsList(Number(goodsCd))
    }
  }
)

watch(isEasm, async newValue => {
  if (newValue) {
    if (isNaN(Number(goodsCd))) return

    const payload = {
      deviceChnId: isDesktop ? 'INT' : 'MOBIL'
    }
    await getGoodsDetail(payload, Number(goodsCd), 'staff').then(async res => {
      if (res.data.resultData) {
        goods.value = res.data.resultData
        await setOptions()
      }
    })
  } else {
    if (goods.value) goods.value.detailInfo.staffDcPrice = 0
    await setOptions()
  }
})

onMounted(async () => {
  mountedInfo.value.goodsCd = undefined
  isFirstEntryInto.value = true
  await init(Number(goodsCd))

  if (Number.isInteger(Number(goodsCd)) && goodsCd.length < 15) {
    commonGoodsBannerStore.commomBannerGoodsList(Number(goodsCd))
  }
})

onActivated(async () => {
  /* if (route.query.popup) {
    router.replace({ name: route.name! })
  } */

  if (Boolean(isFirstEntryInto.value)) {
    mountedInfo.value.goodsCd = route.params.goodsCd as string
  } else {
    onActivatedInit()
  }
})

onDeactivated(() => {
  isFirstEntryInto.value = false
  // clearliveOrdQtyInterval()
  // clearDealTimer()
})

// functions
async function init(goodsCd: number) {
  const ab = variationDetail(45)
  abTestCodeRef.value = ab.variation

  // clearDealTimer()
  clearInfo()
  isImgLoaded.value = false

  await goodsStore.goodsDetailAction(goodsCd)
  bundleInfo.dlvGoodsCd = Number(goodsCd)
  if (isBundleGoods.value && !isSoldOut.value) await bundleDeliverySearch(bundleInfo)

  commonInit()
  setOptions()

  if (isNoSaleMall.value) enterPartner()

  relationGoodsList()
  recommendList()

  if (isLogin.value) getAlarmList(goodsCd, false)

  if (nliveCd.value) {
    await goodsStore.nLiveBroadCastListAction(String(nliveCd.value), Number(goodsCd))
  }

  visitHistory()

  localContributeOrder.value.popupId = contributeOrder.value.popupId
  localContributeOrder.value.popupNm = contributeOrder.value.popupNm
  localContributeOrder.value.dispId = contributeOrder.value.dispId
  localContributeOrder.value.dispNm = contributeOrder.value.dispNm
  localContributeOrder.value.prmtId = contributeOrder.value.prmtId
  localContributeOrder.value.prmtNm = contributeOrder.value.prmtNm
  localContributeOrder.value.areaId = contributeOrder.value.areaId
  localContributeOrder.value.areaNm = contributeOrder.value.areaNm
}

async function onActivatedInit() {
  const currentGoodsCd = route.params.goodsCd
  const beforeGoodsCd = String(goods.value?.detailInfo?.goodsCd)
  const MountedGoodsCd = mountedInfo.value.goodsCd

  if (beforeGoodsCd === currentGoodsCd) {
    // clearDealTimer()
    commonInit()
  }

  if (!isLogin.value) {
    state.isWishBtn = false
  } else {
    getAlarmList(Number(currentGoodsCd), !!route.query.wish)
  }
  if ((MountedGoodsCd === currentGoodsCd && !goods.value && !onWatched.value) || route.query.type === 'order') {
    await init(Number(currentGoodsCd))
  } else if (!history.state.forward) {
    // 뒤로가기 진입이 아닌 경우 기여매장/기획전 세팅
    localContributeOrder.value.popupId = contributeOrder.value.popupId
    localContributeOrder.value.popupNm = contributeOrder.value.popupNm
    localContributeOrder.value.dispId = contributeOrder.value.dispId
    localContributeOrder.value.dispNm = contributeOrder.value.dispNm
    localContributeOrder.value.prmtId = contributeOrder.value.prmtId
    localContributeOrder.value.prmtNm = contributeOrder.value.prmtNm
    localContributeOrder.value.areaId = contributeOrder.value.areaId
    localContributeOrder.value.areaNm = contributeOrder.value.areaNm
  }
}

function clearInfo() {
  goodsInfo.value = undefined
  itemInfo.value = undefined
  giftInfo.value = undefined
  alcoholInfo.value = undefined
  gcGoodsInfo.value = undefined
  togetherView.value = []
  // categoryGoods.value = []
  popularGoods.value = []
  relationGoods.value = []
  state.isWishBtn = false
  state.isBundleDelivery = false
  state.activeTab = TAB_TYPE.TAB1
  nLiveBroadCast.value = undefined
  isTimeOver.value = false
}

// function clearDealTimer() {
//   if (timerState.timer) {
//     delete timerState.callback
//     clearTimer(timerState.timer as number, 'end timer')
//   }
// }

const _callbackClose = () => {
  emit('update:modelValue', false)
}

async function cartModal(cartInformation: any) {
  isShowModalDetailCart.value = true
  cartInfo.value = cartInformation
  cartKey.value++
  _callbackClose()
}

async function cartInterface() {
  const isModalOpen =
    goods.value?.detailInfo?.styleMngYn === 'Y' ||
    goods.value?.giftInfo?.giftBnftList?.some(item => item.displayYn === 'Y' && (item.unitList?.length ?? 0) > 1)
      ? true
      : false
  if (isModalOpen) {
    state.isShowOptions = true
  } else {
    // GoogleAnalyticsCartItem 타입으로 변환
    const info = {
      productNm: goods.value?.detailInfo.productNm,
      brandNm: goods.value?.detailInfo.brandNm, // 브랜드
      brandCd: goods.value?.detailInfo.brandCd, // 브랜드코드
      formStartDttm: goods.value?.brdctInfo?.formStartDttm, // 방송시작시간
      formEndDttm: goods.value?.brdctInfo?.formEndDttm, // 방송종료시간
      staffDcPrice: goods.value?.detailInfo?.staffDcPrice // 방송종료시간
    }
    await hasNotOptionCartEvent(
      emit,
      CART_FLAG.cartGoodsDtl,
      null,
      localContributeOrder.value.dispId,
      localContributeOrder.value.areaId,
      localContributeOrder.value.prmtId,
      abTestCodeRef.value,
      info
    ).then(res => {
      cartModal(res)
    })
  }
}

async function setOptions() {
  if (!goods.value) return

  await optionsStore.optionListAction(Number(goods.value?.detailInfo?.goodsCd))
  goodsInfo.value = {
    instGoodsYn: goods.value?.detailInfo?.instGoodsYn,
    maxOrderPossQty: goods.value?.detailInfo?.maxOrderPossQty,
    custPrchQtyLimitYn: goods.value?.detailInfo?.custPrchQtyLimitYn,
    cnnlCd: goods.value?.detailInfo?.busChnId,
    brdctDate: goods.value?.brdctInfo?.brdctDate,
    cnnlNumCd: goods.value?.brdctInfo?.cnnlNumCd,
    pgmCd: goods.value?.brdctInfo?.pgmCd,
    cartFlag: CART_FLAG.cartGoodsDtl,
    dispTypeCd: goods.value?.detailInfo?.dispTypeCd,
    dutyUseDur: goods.value?.detailInfo?.dutyUseDur,
    mmRntalPrc: goods.value?.detailInfo?.mmRntalPrc,
    noSaleMallYn: goods.value?.detailInfo?.noSaleMallYn,
    stockOutFlag: goods.value?.detailInfo?.stockOutFlag,
    orderPrgrsTypeCd: goods.value?.detailInfo?.orderPrgrsTypeCd,
    marketingScriptInfo: goods.value?.marketingScriptInfo,
    brandNm: goods.value?.detailInfo?.brandNm,
    brandCd: goods.value?.detailInfo?.brandCd,
    formStartDttm: goods.value?.brdctInfo?.formStartDttm,
    formEndDttm: goods.value?.brdctInfo?.formEndDttm,
    salePrice: goods.value?.detailInfo?.salePrice,
    staffDcPrice: goods.value?.detailInfo?.staffDcPrice
  }
  itemInfo.value = {
    detailInfo: goods.value?.detailInfo,
    brdctInfo: goods.value?.brdctInfo
  }
  giftInfo.value = {
    giftBnftList: goods.value?.giftInfo?.giftBnftList,
    adltGoodsYn: goods.value?.detailInfo?.adltGoodsYn
  }

  if (goods.value?.detailInfo?.alcoholicInfo) {
    alcoholInfo.value = goods.value?.detailInfo?.alcoholicInfo
  }

  if (Number(goods.value?.detailInfo?.qstCnt) < 999) {
    state.tabItems[3].number = Number(goods.value?.detailInfo?.qstCnt)
  }

  setGoodsInfo(goodsInfo.value)
}
async function setGoodsInfo(goodsInfo: GoodsInfo) {
  await optionsStore.goodsInfoAction(goodsInfo)
  if (storeGoodsInfo.value) {
    if (route.query.type === 'order' && !isLiveLimit.value && !isSoldOut.value) {
      state.isShowOptions = true
      router.replace({ name: route.name! })
    }
  }
}

function commonInit() {
  uiLayoutStore.setFloatingRightClass('has-bottom2')

  // if (isLiveGoods.value) {
  //   // goodsPurchaseCnt()
  //   // liveOrdQtyInterval.value = setInterval(goodsPurchaseCnt, 300000)
  //   onStartTimer()
  // }

  if (isLiveLimit.value) broadCastingToast()
  if (isSoldOut.value) soldOutToast()
  const params = new URLSearchParams(window.location.search)
  const rcCode = params.get('rccode')
  if (rcCode && (rcCode === 'P01' || rcCode === 'P02')) {
    localContributeOrder.value.recommandType = rcCode
  } else {
    localContributeOrder.value.recommandType = ''
  }
}

// async function onStartTimer() {
//   await rootAppStore.updateCurrentTimeAction()
//   const now = dayjs(currentTime.value)
//   const end = dayjs(goods.value?.brdctInfo?.formEndDttm, 'YYYY-MM-DDTHH:mm:ss')
//
//   const diffSecond = end.diff(now, 'second') % 60
//   const diffMinute = end.diff(now, 'minute')
//
//   startTimer(diffMinute, diffSecond, timerCallback, goods.value?.detailInfo?.productNm)
// }

async function visitHistory() {
  if (!goods.value) return

  const info = goods.value?.detailInfo
  const marketing = goods.value?.marketingScriptInfo

  if (info?.potalSearchBlockYn === 'Y') {
    const source = { meta: [{ name: 'robots', content: 'noindex' }] }
    useHead(source)
  }

  const searchKeyword = contributeSearchKeyword.value

  setDepths([info?.productNm])
  setPageTitle(info?.productNm)

  setCustomMeta({
    'eg:type': 'product',
    'eg:cuid': env.VITE_RECOBELL_CUID,
    'eg:itemId': String(info?.goodsCd),
    'eg:itemName': String(info?.productNm),
    'eg:originalPrice': String(info?.salePrice),
    'eg:salePrice': String(info?.dcPrice),
    // 1depth를 대대카 개념으로 사용하고 있어 실질적 분류를 위해서 추천은 2depth부터 구분하도록 수정
    'eg:category1': String(marketing?.cate2Cd ?? ''),
    'eg:category2': String(marketing?.cate3Cd ?? ''),
    'eg:category3': String(marketing?.cate4Cd ?? ''),
    'eg:category4': String(marketing?.cate5Cd ?? ''),
    'eg:category5': '',

    'eg:newcategory1': String(marketing?.newCategory1),
    'eg:newcategory2': String(marketing?.newCategory2),
    'eg:newcategory3': String(marketing?.newCategory3),
    'eg:newcategory4': String(marketing?.newCategory4),
    'eg:newcategory5': String(marketing?.newCategory5),
    'eg:catalogue': '',
    'eg:brandId': String(info?.brandCd),
    'eg:brandName': String(info?.brandNm),
    'eg:regDate': '',
    'eg:updateDate': '',
    'eg:tcom': info?.busChnId === 'CTCOM' ? 'Y' : 'N',
    'eg:tv': info?.busChnId === 'TV' ? 'Y' : 'N',
    'eg:broad': isLimitGoods.value ? 'B' : 'A',
    'eg:etc1': isLimitGoods.value ? 'B' : 'A',
    'eg:etc2': String(marketing?.easmGoodsYn),
    'eg:etc3': info?.busChnId === 'TV' || info?.busChnId === 'CTCOM' ? 'TV' : 'INT'
  })

  if (isAdltGoods.value && !isAdultCertifiedUser.value) {
    return
  }

  const visitHistory: VisitHistoryProduct = {
    catentryId: info?.goodsCd,
    productName: info?.productNm?.trim(),
    partNumber: info?.goodsCd,
    busChnId: info?.busChnId,
    dispTypeCd: info?.dispTypeCd,
    salePrice: info?.salePrice,
    mmRntalPrc: info?.mmRntalPrc,
    adultItemFlag: info?.adltGoodsYn,
    isSearch: contributeOrder.value.dispId === VIRTUAL_DISP_ID.SEARCH_RESULT ? 'Y' : 'N'
  }
  addVisitHistory(visitHistory)
  addDetailGoogleAnalytics(goods.value, undefined, undefined, searchKeyword)
}

// async function goodsPurchaseCnt() {
//   if (isLiveGoods.value) {
//     const payload = {
//       goodsCd: Number(goods.value?.detailInfo?.goodsCd),
//       busChnId: channelId.value
//     }
//     const { data } = await getGoodsPurchaseCnt(payload)
//     if (data.resultCode === '0000') {
//       state.liveOrdQty = Number(data.resultData?.realtimePurchaseQty)
//       if (state.liveOrdQty > 0) {
//         purchaseToast()
//       }
//     }
//   } else {
//     clearliveOrdQtyInterval()
//   }
// }

// function clearliveOrdQtyInterval() {
//   if (liveOrdQtyInterval.value) clearInterval(liveOrdQtyInterval.value)
// }

async function recommendList() {
  const { variationDetail } = useHackle()
  if (goods.value) {
    const ab = variationDetail(31)
    const variation = ab.variation

    const { data } = await getSearchRecommandGoods(togetherViewCohortParams(goods.value, variation))
    if (data.resultData && data.resultData.length > 0) {
      togetherView.value = data.resultData
    }

    /* const category = await getSearchRecommandGoods(
      categoryGoodsParams(goods.value?.marketingScriptInfo, String(channelId.value))
    )
    if (category.data.resultData && category.data.resultData.length > 0) {
      categoryGoods.value = category.data.resultData
    } */

    const params: GroupCodeGoodsListRequest = {
      goodsCd: goods.value?.detailInfo?.goodsCd,
      useCache: 'N',
      previewMode: 'N'
    }
    const { data: groupCodeData } = await getGroupCodeGoodsList(params)
    if (groupCodeData.resultData && groupCodeData.resultData.gcGoodsInfo) {
      gcGoodsInfo.value = groupCodeData.resultData.gcGoodsInfo
    }

    const popular = await getSearchRecommandGoods(popularGoodsParams(goods.value))
    if (popular.data.resultData && popular.data.resultData.length > 0) {
      popularGoods.value = popular.data.resultData
    }
  }
}

async function relationGoodsList() {
  const brdctInfo = goods.value?.brdctInfo
  if (isBroadCastGoods.value && brdctInfo?.pgmCd) {
    const payload = {
      cnnlNumCd: brdctInfo?.cnnlNumCd,
      pgmCd: brdctInfo?.pgmCd,
      goodsCd: Number(goods.value?.detailInfo.goodsCd)
    }

    const res = await getMediaRelationList(payload, brdctInfo?.brdctDate)
    relationGoods.value = []
    if (res.data.resultData) {
      relationGoods.value = res.data.resultData
    }
  } else {
    relationGoods.value = []
  }
}

function getClassByType() {
  if (broadcastLiveCd.value === 'A') {
    return 'time'
  } else if (broadcastLiveCd.value === 'B') {
    return 'expected'
  }
}

function enterPartner() {
  dialogRef.value?.open({
    message: `${goods.value?.detailInfo?.noSaleMallMsg}\nNSmall에서 구매하시겠어요?`,
    isShowButton: true,
    isShowTwoButton: true,
    firstButtonLabel: '취소',
    secondButtonLabel: '구매하러 가기',
    callbackFunction: (value: boolean) => {
      if (!value) {
        gateStore.modifyPtnCd()
        location.reload()
      }
    }
  })
}

function broadCastingToast() {
  toastHandler(
    `방송 종료되었습니다.<br> 방송 중에만 ${!isConsultantGoods.value ? '구매' : '상담'} 가능합니다.`,
    3000,
    'bottom',
    true
  )
}

function soldOutToast() {
  toastHandler('품절되었습니다.<br> 빠른 시간 내에 상품을 준비하겠습니다.', 3000, 'bottom', true)
}

function purchaseToast() {
  toastHandler(
    `지금 고객님들이 <span style="color: red">${commaizeNumber(Number(state.liveOrdQty))}</span>${
      !isConsultantGoods.value ? '개 구매 중입니다.' : '건 상담 신청 중입니다.'
    }`,
    2000,
    'bottom',
    true
  )
}

async function _onClickBroadCastAlarm() {
  if (!isLogin.value) {
    return router.push({ name: 'CustomerLogin', query: { joinRedirectUri: `/goods/${goodsCd}` } })
  }

  if (state.hasAlarm) {
    const { data } = await deleteBroadCastAlarm({ infrId: infrId.value })
    if (data.resultCode === '0000') {
      state.hasAlarm = false
      infrId.value = 0
      toastHandler('방송 알림이 해제되었습니다.', 1000, 'bottom')
      goodsStore.setBroadCastAlarm(state.hasAlarm, infrId.value, true)
    }
  } else {
    state.isShowBroad = true
  }
}

/** brdct, wish */
async function getAlarmList(goodsCd: number, activated: boolean) {
  await goodsStore.checkBroadWishAction({ goodsCd: goodsCd })
  state.isWishBtn = buttonStateInfo.value?.wishYn === 'Y'
  state.hasAlarm = buttonStateInfo.value?.brdctYn === 'Y'
  infrId.value = Number(buttonStateInfo.value?.infrId)
  goodsStore.setBroadCastAlarm(state.hasAlarm, infrId.value, false)

  if (activated) {
    if (!state.isWishBtn) {
      toggleWish()
    } else {
      customToastStore.open({
        content: TOAST.WISH
      })
    }
    router.replace({ name: route.name! })
  }
}

function broadcastRegisted(broadCastId: number) {
  infrId.value = broadCastId
  state.hasAlarm = true
  goodsStore.setBroadCastAlarm(state.hasAlarm, infrId.value, false)
}

/** 찜 */
async function toggleWish() {
  if (!isLogin.value) {
    return router.push({ name: 'CustomerLogin', query: { joinRedirectUri: `${route.path}?wish=true` } })
  }

  if (!state.isWishBtn) {
    const { data } = await postWishList({ goodsCd: Number(goods.value?.detailInfo?.goodsCd) })

    if (data.resultCode === '0000') {
      state.isWishBtn = true
      customToastStore.open({
        content: TOAST.WISH
      })

      let tollPoint = 0
      const tollPointData = await getUsePossToll()
      if (tollPointData.data.resultData) {
        tollPoint = Number(tollPointData.data.resultData.usePossAmt)
      }

      //GA 찜추가
      addDetailGoogleAnalytics(goods.value!, true, tollPoint, undefined, GA_EVENT_TYPE.ADD_WISH)
    } else if (data.resultCode === '7820') {
      toastHandler(
        '찜한 상품이 100개를 초과하였습니다.<br> 계속 찜하시려면 찜 목록을 정리해 주세요.',
        2000,
        'bottom',
        true
      )
    }
  } else {
    const { data } = await deleteWishList({ goodsCd: [Number(goods.value?.detailInfo?.goodsCd)] })

    if (data.resultCode === '0000') {
      state.isWishBtn = false
      customToastStore.open({
        content: TOAST.UNWISH
      })

      let tollPoint = 0
      const tollPointData = await getUsePossToll()
      if (tollPointData.data.resultData) {
        tollPoint = Number(tollPointData.data.resultData.usePossAmt)
      }

      //GA 찜삭제
      addDetailGoogleAnalytics(goods.value!, true, tollPoint, undefined, GA_EVENT_TYPE.DELETE_WISH)
    }
  }
}

async function bundleDeliverySearch(payload: BundleDeliveryRequest) {
  const { data } = await getBundleDelivery(payload)
  if (data.resultData.list.length > 1) {
    state.isBundleDelivery = true
  }
}

const _onChangeTab = async (id: Key, remtrlPoo: string) => {
  state.activeTab = id
  const offsetEle = detailInfoWrap.value?.getBoundingClientRect()?.top
  const offsetEleAbsolTop = window.scrollY + offsetEle!
  const tabHeight = detailInfoWrap.value?.querySelector('.goods-tab-list')?.clientHeight
  const goodsPlayingEle = document.querySelector('.goods-detail-wrap .goods-playing-wrap')
  const goodsPlayingHeight = goodsPlayingEle ? goodsPlayingEle.clientHeight : 0
  const moHeaderElm = document.querySelector('.mo-header-wrap')
  const headerHeight = moHeaderElm ? moHeaderElm.clientHeight : 0
  const sideEffectHeight = isDesktop.value ? tabHeight! : headerHeight + goodsPlayingHeight

  await nextTick()
  if (remtrlPoo) {
    scrollTo(offsetEleAbsolTop - sideEffectHeight, () => uiScrollStore.setScrollDown())
  } else {
    scrollTo(offsetEleAbsolTop - sideEffectHeight, () => uiScrollStore.setScrollUp())
  }
}

function _onClickDetailInfo(info: { id: Key; type: string }) {
  _onChangeTab(info.id, info.type)
}

function _onChangeTabItems(item: Key) {
  _onChangeTab(item, 'move')
}

const getReviewCount = (count: string) => {
  state.tabItems[2].number = Number(count)
}

const getInquiryCount = (count: string) => {
  state.tabItems[3].number = count
}

function onLoadImg() {
  isImgLoaded.value = true
}
</script>

<template>
  <div class="goods-detail-wrap">
    <VideosPlaying
      v-if="!isDesktop"
      :stopWatch="remainTime"
      :broadcastTimeInfo="broadcastTimeInfo"
      :liveOrdQty="state.liveOrdQty"
    />

    <div class="goods-info-wrap">
      <HeadCopyBanner
        v-if="!isDesktop && headCopyTopBanner && headCopyTopBanner.length > 0"
        class="img-banner top"
        :items="headCopyTopBanner"
      />

      <DetailImg v-if="goods && itemInfo" :stopWatch="remainTime" :items="itemInfo" @on-load="onLoadImg" />
      <SkeletonGoodsDetailImg v-else-if="!isImgLoaded" />

      <div class="goods-detail-info">
        <template v-if="goods && isImgLoaded">
          <HeadCopyBanner
            v-if="isDesktop && headCopyTopBanner && headCopyTopBanner.length > 0"
            class="img-banner top"
            :items="headCopyTopBanner"
          />
          <div class="broadcast-info" v-if="channelId === 'TV' || channelId === 'CTCOM'">
            <div class="inner-wrap">
              <span :class="['onair', channelId === 'CTCOM' && 'shop-plus']" v-if="isLiveGoods">방송중</span>
              <span class="basic">TV {{ channelId === 'CTCOM' ? 'Shop+' : 'LIVE' }}</span>
              <span :class="getClassByType()" v-if="!isTimeOver && broadcastLiveCd !== 'C'"
                >{{ isLiveState }} <em v-if="getClassByType() === 'time'">남음</em></span
              >
              <span class="end" v-else-if="isTimeOver && broadcastLiveCd !== 'C'">생방송이 종료 되었습니다.</span>
              <button
                v-if="goods?.brdctInfo"
                type="button"
                class="broadcast-btn"
                :class="[hasAlarm && 'is-active']"
                @click="_onClickBroadCastAlarm()"
                :disabled="broadCastAlarmDisabled"
              >
                <ModalBroadCast v-model="state.isShowBroad" @update-value="broadcastRegisted" :cart-goods="goods" />
                <span class="icon">방송알림</span>
              </button>
            </div>
          </div>

          <DetailInfo :items="goods" :isDesktop="isDesktop" @update:model-value="_onClickDetailInfo" />
          <div v-if="!isOnSiteSaleGoodsYn" class="btn-share-wish-wrap">
            <ModalShare
              v-if="goods"
              v-model="state.isShowShare"
              :items="goods"
              :type="!isConsultantGoods ? 'product' : 'intangibleProduct'"
              :image-url="goodsImagePath"
            />
            <button type="button" class="btn-icon-share" @click="state.isShowShare = true">
              <i>공유하기</i>
            </button>
            <button
              type="button"
              :class="[state.isWishBtn ? 'btn-icon-wish on' : 'btn-icon-wish']"
              @click="toggleWish()"
            >
              <i>찜하기</i>
            </button>
          </div>
          <Delivery v-if="goods" :items="goods" :headCopy="headCopyDeliveryText" :bundle="state.isBundleDelivery" />
          <HeadCopyBanner
            v-if="headCopyMidBanner && headCopyMidBanner.length > 0"
            class="img-banner first"
            :items="headCopyMidBanner"
          />
          <Options
            v-if="isDesktop && storeOptionsInfo && goodsInfo"
            class="desktop-only-option"
            :goodsType="isConsultantGoods"
            :abTestCode="abTestCodeRef"
            :brdctLimit="isLimitGoods"
            :contribute-order="localContributeOrder"
            :recomdeal-yn="recomdealYn"
          />
        </template>
        <SkeletonGoodsDetailInfo v-if="!goods" />
      </div>
    </div>

    <div class="goods-broadcast-recommend" v-if="goods && isImgLoaded && relationGoods && relationGoods.length > 0">
      <GoodsRecommend title="함께 보면 좋은 상품" :items="relationGoods" />
    </div>
    <GoodsMdRecommend
      v-if="goods && isImgLoaded && recommendGdsText"
      :title="recommendGdsText"
      :items="recommendGdsList"
      :bannerType="recommendGdsType"
    />

    <HeadCopyBanner
      v-if="goods && isImgLoaded && headCopyBottomBanner && headCopyBottomBanner.length > 0"
      class="img-banner second"
      :items="headCopyBottomBanner"
    />

    <div ref="detailInfoWrap" v-if="goods && isImgLoaded" class="goods-detail-info-wrap">
      <div class="col detail-info">
        <div class="goods-tab-wrap">
          <ul class="goods-tab-list">
            <li
              v-for="item in state.tabItems"
              :key="item.id"
              :class="{
                active: item.id === state.activeTab
              }"
            >
              <button type="button" @click="_onChangeTab(item.id, '')">
                {{ item.title
                }}<span class="num" v-if="Number(item.number) > 0">{{
                  Number(item.number) > 999 ? '999+' : item.number
                }}</span>
              </button>
            </li>
          </ul>

          <div class="goods-tab-content" v-if="state.activeTab === TAB_TYPE.TAB1">
            <div class="headcopy-intro-wrap" v-if="headCopyHtmlInfo">
              <div class="intro-contents" v-html="headCopyHtmlInfo.htmlCtnt"></div>
            </div>
            <InfoAlcohol v-if="alcoholInfo" :items="alcoholInfo" />
            <!-- 사은품 관련 -->
            <InfoGift v-if="giftInfo" :items="giftInfo" />
            <InfoDetailImg
              v-if="String(goods?.detailInfo?.goodsSpcsHtml)"
              :goodsSpcsHtml="String(goods?.detailInfo?.goodsSpcsHtml)"
            />
            <GoodsMdList
              v-if="recommendTopText"
              :title="recommendTopText"
              :items="recommendTopSeq"
              :bannerType="recommendTopType"
            />
            <Nlive v-if="!isDesktop && nLiveBroadCast && nLiveBroadCast.length > 0" />
            <ReviewList
              v-if="goods"
              :catGb="goods.detailInfo?.catGb"
              :goodsCd="goods.detailInfo?.goodsCd"
              :dispTypeCd="goods.detailInfo?.dispTypeCd"
              :adltGoodsYn="goods.detailInfo?.adltGoodsYn"
              :productNm="goods.detailInfo?.productNm"
              :viewType="'summary'"
              @tab-change="_onChangeTabItems"
              @count-review="getReviewCount"
            />
          </div>

          <div class="goods-tab-content" v-if="state.activeTab === TAB_TYPE.TAB2">
            <InfoTable :channelType="isBroadCastGoods" :goodsCd="Number(goods?.detailInfo?.goodsCd)" />
          </div>

          <div class="goods-tab-content" v-if="state.activeTab === TAB_TYPE.TAB3">
            <ReviewList
              v-if="goods"
              :catGb="goods.detailInfo?.catGb"
              :goodsCd="goods.detailInfo?.goodsCd"
              :dispTypeCd="goods.detailInfo?.dispTypeCd"
              :adltGoodsYn="goods.detailInfo?.adltGoodsYn"
              :productNm="goods.detailInfo?.productNm"
              @count-review="getReviewCount"
            />
          </div>

          <div class="goods-tab-content" v-if="state.activeTab === TAB_TYPE.TAB4">
            <InquiryList
              :catGb="String(goods?.detailInfo?.catGb)"
              :goodsCd="Number(goods?.detailInfo?.goodsCd)"
              :productNm="String(goods?.detailInfo?.productNm)"
              :adltGoodsYn="String(goods?.detailInfo?.adltGoodsYn)"
              @count-qna="getInquiryCount"
            />
          </div>
        </div>
      </div>
      <div class="col option-info">
        <!--
           [mobile]: bottom layer
           [desktop]: right-option
        -->
        <Options
          v-if="isDesktop && storeOptionsInfo && goodsInfo"
          class="right-option"
          :goodsType="isConsultantGoods"
          :viewType="viewType.RIGHT"
          :abTestCode="abTestCodeRef"
          :brdctLimit="isLimitGoods"
          :contribute-order="localContributeOrder"
          :recomdeal-yn="recomdealYn"
        />
        <ModalOptions
          v-else-if="!isDesktop"
          v-model="state.isShowOptions"
          :goods="goodsInfo!"
          :options="storeOptionsInfo"
          :abTestCode="abTestCodeRef"
          :brdctLimit="isLimitGoods"
          :contribute-order="localContributeOrder"
          :recomdeal-yn="recomdealYn"
        />
        <ModalDetailCart
          v-model="isShowModalDetailCart"
          :carts="Array.isArray(cartInfo) && cartInfo[0]?.goodsCd ? cartInfo[0] : undefined"
          :key="cartKey"
        />
        <Kakao v-if="!isDirectVisitEnabled" />
        <GoodsButtonGroup
          class="only-mobile"
          v-if="!isDesktop && goodsInfo"
          @on-present="state.isShowOptions = true"
          @on-cart="cartInterface"
          @on-buy="state.isShowOptions = true"
          :brdctLimit="isLimitGoods"
          :recomdeal-yn="recomdealYn"
        />
      </div>
    </div>

    <div v-if="goods && isImgLoaded" class="goods-bottom-contents">
      <div class="goods-recommend-list">
        <GoodsRecommend
          v-if="gcGoodsInfo && (gcGoodsInfo.gcGoodsList?.length ?? 0) > 0"
          title="같이 담기 좋은 상품"
          :items="gcGoodsList"
          :prmtId="gcGoodsInfo.prmtId"
        />

        <GoodsRecommend
          v-if="togetherView && togetherView.length > 0"
          title="다른 고객이 함께 구매했어요"
          :items="togetherView"
          :rccode="isDesktop ? RC_CODE.DETAIL_VIEW_PC : RC_CODE.DETAIL_VIEW_MO"
        />

        <!-- <GoodsRecommend
          v-if="categoryGoods && categoryGoods.length > 0"
          title="카테고리 추천 상품"
          :items="categoryGoods"
          :rccode="isDesktop ? RC_CODE.DETAIL_PURCHASE_PC : RC_CODE.DETAIL_PURCHASE_MO"
        /> -->

        <GoodsRecommend
          v-if="popularGoods && popularGoods.length > 0"
          title="이런 상품은 어때요?"
          :items="popularGoods"
          :rccode="isDesktop ? RC_CODE.DETAIL_POPULAR_BROAD_PC : RC_CODE.DETAIL_POPULAR_BROAD_MO"
        />
      </div>
    </div>

    <button type="button" @click="purchaseToast" hidden>
      <i class="blind">구매 수량 정보</i>
    </button>
  </div>
  <Dialog ref="dialogRef" />
  <CommonGoodsBanner />
</template>

<style lang="scss">
@use '~/pages/goods/scss/goods.scss';
</style>
